<template>
  <div class="product-form">
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <div class="form-group">
        <div
          v-show="!hasOnlyDefaultVariant && !product.isComingSoon"
          class="form-group__field product-form__variants"
        >
          <template v-if="!isQuickView">
            <size-toggle
              v-if="!productGiftCard"
              :hide-size-toggle="hideSizeToggle"
              :product="product"
              @product-id-update="(val) => $emit('product-id-update', val)"
              @product-variant-update="productVariantUpdated"
            />

            <div
              v-else
              class="size-toggle"
            >
              <p
                class="size-toggle__title text-body-2xs"
                v-text="$string('product.options.gift_card_option')"
              />
            </div>
          </template>

          <size-selector
            :current-variant="currentVariantId"
            primary-vector="Waist"
            :product="product"
            :product-options="productOptions"
            :variants="product.variants"
            @product-variant-update="productVariantUpdated"
          />

          <a
            v-if="sizeGuideLink"
            :href="sizeGuideLink"
            class="product-form__size-guide text-body-2xs-link"
            target="_blank"
            v-text="$string('product.form.size_guide')"
          />
        </div>

        <!-- Free gift with purchase -->
        <gift-with-purchase-trigger
          class="product-form__gift"
          :product="product"
        />

        <div class="form-group__footer">
          <btn
            v-if="product.isComingSoon"
            block-class="product-form"
            :disabled="!comingSoonLink"
            :label="$string('product.form.coming_soon_product')"
            :modifiers="['block']"
            :show-icon="true"
          />

          <!-- Back in Stock Widget -->
          <btn
            v-else-if="!product"
            class="
              product-form__add-to-cart
              product-form__add-to-cart--loading
              product-form
            "
            block-class="product-form"
            :disabled="true"
            :label="addToCartText"
            modifiers="block"
            :show-state-icon="state === 'adding'"
            type="button"
          >
            <template #icon>
              <component :is="addToCartIcon" />
            </template>

            <template #stateIcon>
              <icon-loading />
            </template>
          </btn>

          <div
            v-else-if="showAddToCart"
            class="product-form__cta-container"
          >
            <btn
              class="product-form__add-to-cart product-form"
              block-class="product-form"
              :label="addToCartText"
              :modifiers="acceleratedCheckoutButton ? 'outlined' : 'block'"
              :show-state-icon="state === 'adding'"
              type="submit"
            >
              <template #animatedIconOne>
                <component :is="addToCartIcon" />
              </template>

              <template #stateIcon>
                <icon-loading />
              </template>
            </btn>

            <!-- Accelerated Checkout Button -->
            <btn
              v-if="acceleratedCheckoutButton"
              class="product-form__accelerated-button product-form"
              block-class="product-form"
              :label="$string('product.cta.buy_now')"
              modifiers="block"
              :show-state-icon="state === 'adding'"
              type="button"
              @click.prevent="handleAcceleratedCheckout"
            >
              <template #stateIcon>
                <icon-loading />
              </template>
            </btn>
          </div>

          <btn
            v-else-if="!hasOnlyDefaultVariant && !currentVariantId"
            block-class="product-form"
            :label="getProductType"
            :modifiers="['block']"
            :disabled="true"
            :show-icon="false"
          />

          <btn
            v-else
            block-class="product-form"
            :label="$string('product.form.coming_soon')"
            :modifiers="['block']"
            :disabled="true"
            :show-icon="false"
          />
        </div>
      </div>
    </form>

    <p
      v-if="error"
      class="product-form__error text-body-m"
      v-text="error"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Array} productOptions - Array of product option objects.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {String} colourSwatchName - Colour swatch name of the product.
 * @param {Boolean} isQuickView - Indicates the product form is nested within
 * the quick view component.
 * @param {String} comingSoonLink - Coming soon landing page link.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import aa from "search-insights"

import Btn from '~global/btn/btn'
import GiftWithPurchaseTrigger from '~async/gift-with-purchase-trigger/gift-with-purchase-trigger'
import SizeSelector from '~async/size-selector/size-selector'
import SizeToggle from '~async/size-toggle/size-toggle'

import IconLoading from '~icons/misc/loading.svg'
import IconTick from '~icons/misc/tick.svg'
import IconBag from '~icons/misc/bag.svg'

import { getHiddenSalePrices } from '~/helpers/general'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    IconLoading,
    IconBag,
    IconTick,
    GiftWithPurchaseTrigger,
    SizeSelector,
    SizeToggle,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        hasOnlyDefaultVariant: false,
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    productOptions: {
      type: Array,
      default: () => [],
    },
    hideSizeToggle: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    colourSwatchName: {
      type: String,
      default: '',
    },
    productGiftCard: {
      type: Boolean,
      default: false,
    },
    isQuickView: {
      type: Boolean,
      default: false,
    },
    acceleratedCheckoutButton: {
      type: Boolean,
      default: false,
    },
    hiddenSalePrice: {
      type: String,
      default: '',
    },
    comingSoonLink: {
      type: String,
      default: '',
    },
    sizeGuideLink: {
      type: String,
      default: '',
    },
  },

  emits: ['update-variant', 'product-id-update'],

  data() {
    return {
      currentVariantId: 0,
      error: '',
      lineItemProperties: {
        _dist_id_one: false,
        _dist_id_two: false,
        colour_swatch_name: this.colourSwatchName,
      },
      quantity: 1,
      state: 'ready',
      timeout: {},
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Controls whether to display the add to cart button.
     */
    showAddToCart() {
      if (this.hasOnlyDefaultVariant && this.product?.available) {
        return true
      }

      if (this.variant?.available) {
        return true
      }

      return false
    },

    /**
     * Compute add to cart icon.
     * @returns {String}
     */
    addToCartIcon() {
      return this.state === 'added' ? IconTick : IconBag
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant?.available) {
        return this.$string('product.cta.select_size')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute has only default variant from Liquid and product objects.
     * @returns {Boolean}
     */
    hasOnlyDefaultVariant() {
      return this.liquid.hasOnlyDefaultVariant || this.product.hasOnlyDefaultVariant
    },


    /**
     * Check type of product if this is a gift, to change cta text.
     * @returns {String}
     */
    getProductType() {
      if (!this.productGiftCard) {
        return this.$string('product.cta.select_size')
      }

      return this.$string('product.cta.select_amount')
    },

    queryID() {
      return this.isQuickView ?
        this.product['__queryID'] :
        new URLSearchParams(location.search).get('queryID')
    },
  },

  watch: {

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromId(this.currentVariantId) ?? false

      this.$emit('update-variant', variant)
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      this.getCurrentVariant()
      this.quantity = 1
    },

    variant: {
      immediate: true,
      deep: true,
      handler(variant) {
        if (!variant){
          this.lineItemProperties._dist_id_one = false
          this.lineItemProperties._dist_id_two = false
          return
        }

        const distIdOne = variant.metafields.find(field => field.name === 'dist_id_one')
        const distIdTwo = variant.metafields.find(field => field.name === 'dist_id_two')

        this.lineItemProperties._dist_id_one = distIdOne?.value ?? false
        this.lineItemProperties._dist_id_two = distIdTwo?.value ?? false
      },
    },
  },

  mounted(){
    this.getCurrentVariant()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Updates the currentVariantId when a new variant is selected.
     * @param {Object} val - Product variant object.
     */
    productVariantUpdated(val) {
      if (!val) {
        this.currentVariantId = 0
        return
      }
      this.currentVariantId = val.id
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      const variant = this.getVariantFromUrl()
      if(!variant){
        this.currentVariantId = 0
        return false
      }

      this.currentVariantId = variant.id
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromId(variantId)
    },

    /**
     * Get variant object from variant ID.
     * @returns {Object}
     */
    getVariantFromId(variantId) {
      return this.product.variants.find((variant) => {
        return variant.id === variantId
      })
    },

    /**
     * Handle quantity selector update.
     * @param {String} quantity - New quantity.
     */
    handleQuantityUpdate(quantity) {
      this.quantity = quantity
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (this.product?.isComingSoon && this.comingSoonLink) {
        window.location = this.comingSoonLink
        return
      }

      if (this.product?.isComingSoon) {
        return
      }

      if (!this.variant) {
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      /**
       * On the quick view product form, register the nosto add to cart event
       * for nosto analytics.
       */
      if (this.isQuickView) {
        const event = new CustomEvent('quick-view-add', {
          detail: {
            productId: this.product.id,
          },
        })
        window.dispatchEvent(event)
      }

      if (this.hiddenSalePrice) {
        const hiddenSalePrices = getHiddenSalePrices(this.product.tags)
        this.lineItemProperties = {
          ...this.lineItemProperties,
          ...hiddenSalePrices,
        }
      }

      const items = [
        {
          id: this.variant.id,
          properties: this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.handleAlgoliaEvents()

        this.state = 'added'
        this.handleCartToggle()

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

      } catch (error) {
        this.state = 'error'

        if (error.length) {
          this.error = error
          return
        }

        reportError('Failed to add product to cart', error)
      }
    },

    /**
     * Send conversion events to algolia.
     */
    handleAlgoliaEvents() {
      const variantPrice = (this.variant?.price?.amount || this.product?.price?.amount) / 100
      const eventPayload = {
        eventName: 'Product Converted',
        index: this.$variable('algolia.indexes.products'),
        objectIDs: [this.product.id.toString()],
        value: variantPrice * this.quantity,
        currency: this.$variable('store.currency'),
      }

      this.queryID ?
        aa('purchasedObjectIDsAfterSearch', {
          ...eventPayload,
          queryID: this.queryID,
          objectData: [
            {
              queryID: this.queryID,
              price: variantPrice,
              quantity: this.quantity,
            },
          ],
        }) :
        aa('purchasedObjectIDs', {
          ...eventPayload,
          objectData: [
            {
              price: variantPrice,
              quantity: this.quantity,
            },
          ],
        })
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     */
    handleCartToggle() {
      this.loadComponent('cart-drawer')
      this.openOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Handle accelerated checkout button click action with permalink.
     * See more;
     * https://community.shopify.com/c/shopify-design/cart-use-permalinks-to-pre-load-the-cart/td-p/613702#adding-a-buy-this-link-to-a-blog-post
     */
    async handleAcceleratedCheckout() {
       if (!this.currentVariantId) {
         return
       }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (this.hiddenSalePrice) {
        const hiddenSalePrices = getHiddenSalePrices(this.product.tags)
        this.lineItemProperties = {
          ...this.lineItemProperties,
          ...hiddenSalePrices,
        }
      }

      const items = [
        {
          id: this.currentVariantId,
          properties: this.lineItemProperties,
          quantity: 1,
        },
      ]

      try {
        await this.addItem(items)
        this.handleAlgoliaEvents()

        // Redirect to checkout after adding to cart to simulate cart permalink.
        window.location.href = `${this.$variable('routes.normalisedRoot')}checkout`
      } catch (error) {
        this.state = 'error'

        if (error.length) {
          this.error = error
          return
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
